import { Download } from 'lucide-react'

import { adminProspects } from 'rails_routes'
import { useWindowLocation } from 'utils/windowLocation'

import { type Quote } from './types'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { Banner, ErrorListBanner } from '../reusable/Banner'

function BackToPreviousScreen ({ handleBack }: { handleBack: () => void }) {
  return (
    <button className='btn-outline' type='button' onClick={handleBack}>
      Back to previous screen
    </button>
  )
}

function BackToProspectButton ({ prospectId }: { prospectId: string }) {
  const { assign } = useWindowLocation()

  return (
    <button
      className='btn-outline'
      type='button'
      onClick={() => {
        assign(adminProspects.show({ id: prospectId }))
      }}
    >
      Back to prospect page
    </button>
  )
}

function QuotePendingButton () {
  return (
    <button className='btn-primary' disabled type='button'>
      <Download size={16} className='mr-2' />
      Quote pending
    </button>
  )
}

export function QuoteErrorBanner ({
  quoteErrors,
}: {
  quoteErrors?: string[] | null;
}) {
  return (
    <ErrorListBanner
      errors={quoteErrors || []}
      titleText='Your quote could not be created'
      genericErrorMessage={`There ${
        quoteErrors && quoteErrors.length > 1 ? 'were' : 'was'
      } ${quoteErrors ? quoteErrors.length : 'unknown'} ${
        quoteErrors && quoteErrors.length > 1 ? 'errors' : 'error'
      }
        encountered while generating the quote.
        ${
          quoteErrors && quoteErrors.length > 0
            ? ' Please refer to the errors below:'
            : ' Please try again or contact support for further assistance.'
        }`}
    />
  )
}

export function QuoteProcessingBanner ({ short }: { short?: boolean }) {
  return (
    <Banner type='attention' titleText='Your quote is being created!'>
      <p>
        You'll be notified via Slack as soon as your quote is done processing.
        {!short &&
          'You can find this quote on the prospect page under the Quotes table.'}
      </p>
    </Banner>
  )
}

type Props = {
  quote: Quote;
  quoteAPIError?: GrapeAPIErrorType | null;
  handleBack: () => void;
}

function getQuoteResultComponents ({ quote, quoteAPIError, handleBack }: Props) {
  let banner
  let footer
  if (quote.status === 'failed' || quoteAPIError) {
    if (quote.status === 'failed') {
      banner = <QuoteErrorBanner quoteErrors={quote.quote_errors} />
    } else if (quoteAPIError) {
      banner = <QuoteErrorBanner quoteErrors={quoteAPIError.errors} />
    }

    footer = (
      <>
        <QuotePendingButton />
        <BackToPreviousScreen handleBack={handleBack} />
        <BackToProspectButton prospectId={quote.quoting_prospect_id} />
      </>
    )
  } else if (quote.status === 'processed') {
    banner = (
      <Banner type='success' titleText='Your quote is ready to download!'>
        <p>
          Download your quote by clicking “Download” below. The quote can also
          be viewed and downloaded from the prospect page in the Quotes table.
        </p>
      </Banner>
    )
    footer = (
      <>
        <a
          href={quote.download_link ?? undefined}
          className={quote.status !== 'processed' ? 'btn-primary pointer-events-none opacity-50' : 'btn-primary'}
          download
        >
          <Download size={16} className='mr-2' />
          Download quote
        </a>
        <BackToProspectButton prospectId={quote.quoting_prospect_id} />
      </>
    )
  } else if (quote.status === 'processing' || quote.status === 'unprocessed') {
    banner = <QuoteProcessingBanner />
    footer = (
      <>
        <QuotePendingButton />
        <BackToProspectButton prospectId={quote.quoting_prospect_id} />
      </>
    )
  }
  return { banner, footer }
}

export function QuoteCreateResult ({ quote, quoteAPIError, handleBack }: Props) {
  const { banner, footer } = getQuoteResultComponents({
    quote,
    quoteAPIError,
    handleBack,
  })

  return (
    <div className='space-y-6'>
      {banner}
      <footer className='flex items-center gap-x-2'>{footer}</footer>
    </div>
  )
}
